<script lang="ts" setup>
import ArrowAccordion from 'assets/icons/arrows/16/chevron-down.svg?component'
import ArrowRight from 'assets/icons/arrows/16/arrow-right.svg?component'
import Accordion from '~/components/ui/accordion/Accordion.vue'
import AppButton from '~/components/ui/buttons/AppButton.vue'
import useAccountStore from '~/shared/store/modules/account'
import type { IBalance } from '~/shared/interfaces/account'
import type { IAccordionToggleResult } from '~~/shared/interfaces/ui/accordion'

const accountStore = useAccountStore()

const balanceEntities = ref<(IBalance & { isOpen: boolean })[]>([])
balanceEntities.value = accountStore.balance.map((el) => ({
  ...el,
  isOpen: false,
}))

function toggleAccordion({
  accordionPosition,
  isOpen,
}: IAccordionToggleResult) {
  balanceEntities.value.forEach((el) => (el.isOpen = false))
  balanceEntities.value[accordionPosition].isOpen = isOpen
}
</script>

<template>
  <div class="navigation-popup__entities" v-if="balanceEntities.length">
    <template v-for="(item, index) in balanceEntities" :key="`point_${index}`">
      <Accordion
        v-if="index < 5"
        :accordion-item="{
          isOpen: item.isOpen,
          title: item.name,
        }"
        accordion-color="transparent"
        @set-accordion-section-status="toggleAccordion"
        :accordion-position="index"
        :arrow-icon="ArrowAccordion"
        :class="item.overdueDebt && 'debt'"
        :is-ellipsis-title="true"
      >
        <template v-slot:accordionContent>
          <div class="entity-info">
            <div class="entity-info__inn">
              <span v-if="item.kpp"
                >ИНН/КПП: {{ item.inn }}/{{ item.kpp }}</span
              >
              <span v-else>ИНН: {{ item.inn }}</span>
            </div>
            <div class="entity-info__balance">
              <div class="entity-info__item">
                <div class="entity-info__item-container">
                  <span>Кредит-лимит</span>
                  <span>{{ item.totalLimit ?? '0.00' }}&nbsp;₽</span>
                </div>
              </div>
              <div
                class="entity-info__item"
                :class="[
                  item.freeLimit && !item.overdueDebt && 'active',
                  item.overdueDebt && 'disabled',
                ]"
              >
                <div class="entity-info__item-container">
                  <span>Доступно</span>
                  <span>{{ item.freeLimit ?? '0.00' }}&nbsp;₽</span>
                </div>
              </div>
              <div
                class="entity-info__item"
                :class="[
                  item.debt && !item.overdueDebt && 'debt',
                  item.overdueDebt && 'overdue-debt',
                ]"
              >
                <div class="entity-info__item-container">
                  <span>Задолженность</span>
                  <span>{{ item.debt ?? '0.00' }}&nbsp;₽</span>
                </div>
                <div
                  class="entity-info__item-container"
                  v-if="item.overdueDebt"
                >
                  <span>Просрочено</span>
                  <span>{{ item.overdueDebt }}&nbsp;₽</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Accordion>
    </template>
  </div>
  <div class="navigation-popup__bottom">
    <NuxtLink to="/account/finance">
      <AppButton
        type="neutral"
        size="s"
        text="Перейти в финансы"
        :icon="ArrowRight"
        gap="8px"
        direction="row-reverse"
      />
    </NuxtLink>
  </div>
</template>

<style lang="scss" scoped>
@use 'assets/styles/components/navigation/navigation-popup.scss';

.accordion {
  padding-bottom: 17px;

  &:last-child {
    padding-bottom: 12px;
  }

  &:not(:first-child) {
    :deep(.accordion-item__header) {
      padding-top: 16px !important;
    }
  }

  :deep(.accordion-item) {
    .accordion-item__header {
      padding: 0;

      .accordion-item__header-title {
        @include font(14px, 16px, 500);
        color: $content_primary;
      }
    }
  }

  &.debt {
    :deep(.accordion-item__header-title) {
      color: $control-content_negative !important;
    }
  }
}

.entity-info {
  text-align: left;
  padding-top: 16px;

  &__inn {
    @include font(14px, 16px);
    color: $content_secondary;
  }

  &__balance {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    border-radius: 6px;
    background-color: $bg_light-grey;
    padding: 8px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    &:first-child {
      padding: 7px 11px;
      border: 1px solid $control-border_grey;
      background-color: $bg_page;
    }

    &.active {
      background-color: $bg_success;
    }

    &.disabled {
      background-color: $bg_border;

      span {
        color: $content_secondary;

        &:last-child {
          text-decoration: line-through;
        }
      }
    }

    &.debt {
      background-color: $bg_warning;
    }

    &.overdue-debt {
      background-color: $bg_danger;

      .entity-info__item-container:nth-child(2) {
        color: $content_negative;
      }
    }

    &-container {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    span {
      white-space: nowrap;

      &:nth-child(odd) {
        @include font(14px, 16px);
      }

      &:nth-child(even) {
        @include font(16px, 20px, 500);
      }
    }
  }
}
</style>
